import React from 'react';
import {
  Body,
  LinkButton,
  StaticNotification,
  timeUtils,
} from '@sato/components';
import { useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { IWidget, ParsedEntry } from '@/contentful/types';

import { api } from '@api';
import { Link } from '@components/common/Link';
import {
  CustomerBenefitOutgoingClickEvent,
  GA_EVENT_TYPES,
} from '@models/analyticsEvents.types';
import { contractStore } from '@stores/contractStore';
import { localizationStore, t } from '@stores/localization';
import { userStore } from '@stores/userStore';
import { sendGoogleAnalyticsEvent } from '@utils/analytics.utils';
import { isLocalOrTestEnv } from '@utils/featureFlags';

type CustomerBenefitWidgetParameters = {
  partner: string;
  type: string;
  linkUrl: string;
};

const isValidParameters = (
  parameters: Record<string, any> | undefined
): parameters is CustomerBenefitWidgetParameters =>
  typeof parameters === 'object' &&
  'partner' in parameters &&
  'type' in parameters &&
  'linkUrl' in parameters;

export const CustomerBenefit = observer(
  ({ parameters }: ParsedEntry<IWidget>) => {
    const isValid = isValidParameters(parameters);

    const { data: benefit } = useQuery(
      ['exchangeBenefit', contractStore.selectedContractId],
      async () => {
        // Should never happen since isValid is part of query's enabled clause
        if (!isValid) return;

        const { data } = await api.user.getBenefit({
          partner: parameters.partner,
          type: parameters.type,
        });

        return data;
      },
      {
        enabled: userStore.user?.isExchangeCustomer && isValid,
      }
    );

    if (!isValid) {
      if (isLocalOrTestEnv) {
        console.error(
          '[CustomerBenefit] Invalid parameters in Contentful widget'
        );
      }

      return null;
    }

    if (benefit === null || benefit?.status === 'EXPIRED') {
      return (
        <StaticNotification
          icon="info"
          body={t('benefit_expired_or_not_entitled_to')}
        />
      );
    }

    if (benefit?.status === 'ACTIVE') {
      const bodyButtonUrl = parameters.linkUrl?.replace(
        '{code}',
        benefit.benefitCode
      );

      return (
        <div>
          <LinkButton
            className="my-4"
            linkComponent={Link}
            to={bodyButtonUrl}
            onClick={() => {
              sendGoogleAnalyticsEvent<CustomerBenefitOutgoingClickEvent>({
                clickTarget: bodyButtonUrl,
                event: GA_EVENT_TYPES.CUSTOMER_BENEFIT_OUTGOING_CLICK,
                module: 'edut_tapahtumat',
              });
            }}
            openInNewTab
          >
            {t('benefit_redeem')}
          </LinkButton>

          <Body>
            {t('benefit.personal_code')} <b>{benefit.benefitCode}</b>
          </Body>

          <Body>
            {t('benefit.unused_amount')} {benefit.benefit}€
          </Body>

          <Body>
            {t('benefit.expiry')}{' '}
            {timeUtils.formatDate({
              date: benefit.expiryDate,
              dateFormat: timeUtils.DATE_FORMATS.DATE,
              locale: localizationStore.language,
            })}
          </Body>
        </div>
      );
    }

    return <Body>{t('personal_benefit_used')}</Body>;
  }
);
